import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { create } from '@bufbuild/protobuf'
import { Skeleton } from 'skullet/react'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { SkeletonBar } from '@/components/shared/skeleton-bar'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Check, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatCurrency } from '@/lib/currency-utils'
import { formatFullDate } from '@/lib/date-utils'
import {
  INTERVAL_LABELS,
  PLAN_LABELS,
  perMonthEquivalentCents,
} from '@/components/subscription/checkout/order-summary-model'
import {
  PlanKeyEnum,
  BillingIntervalEnum,
  PlanOptionSchema,
  type OrganizationBillingState,
  type PlanOption,
  type PreviewPlanChangeResponse,
} from '@/gen/cortado/v1/subscriptions_pb'
import {
  useListPlanOptions,
  usePreviewPlanChange,
  useChangeSubscriptionPlan,
} from '@/stores/billing.store'

const SAMPLE_INTERVAL_OPTIONS: PlanOption[] = [
  { billingInterval: BillingIntervalEnum.BILLING_INTERVAL_MONTHLY, priceCents: 22800n, priceFormatted: '$228.00', intervalMonths: 1 },
  { billingInterval: BillingIntervalEnum.BILLING_INTERVAL_SEMIANNUAL, priceCents: 129960n, priceFormatted: '$1,299.60', intervalMonths: 6, savingsLabel: '5% off' },
  { billingInterval: BillingIntervalEnum.BILLING_INTERVAL_ANNUAL, priceCents: 246240n, priceFormatted: '$2,462.40', intervalMonths: 12, savingsLabel: '10% off' },
].map((option) => create(PlanOptionSchema, { planKey: PlanKeyEnum.PLAN_KEY_STANDARD, ...option }))

interface PlanIntervalSwitcherProps {
  organizationId: string
  billingState: OrganizationBillingState
  // 'subscribe': no live subscription yet - selecting an interval starts
  // checkout. 'change': a live subscription exists - selecting a different
  // interval previews then commits a PATCH via ChangeSubscriptionPlan.
  mode: 'subscribe' | 'change'
  onSubscribe: (planKey: PlanKeyEnum, billingInterval: BillingIntervalEnum, option: PlanOption) => void
  // Optional highlight for a parent-owned selection (e.g. CheckoutFlow
  // waits for an explicit confirm before opening Stripe Billing checkout). Independent of
  // option.isCurrent, which is the live subscription's interval.
  highlightedPlanKey?: PlanKeyEnum
  highlightedBillingInterval?: BillingIntervalEnum
  // 'compact' tightens the cards for the ~700px onboarding/settings containers.
  variant?: 'default' | 'compact'
}

interface PreviewRequestKey {
  organizationId: string
  mode: PlanIntervalSwitcherProps['mode']
  planKey: PlanKeyEnum
  billingInterval: BillingIntervalEnum
  generation: number
}

interface ResolvedPreview {
  request: PreviewRequestKey
  response: PreviewPlanChangeResponse
}

/**
 * F6: three interval cards per plan (R-3 PIF savings, per-month equivalent,
 * "plus applicable tax" per R-110). When plan_is_assigned (R-71) the plan is
 * shown, not chosen - no Founder/CCC selector renders.
 */
export function PlanIntervalSwitcher({
  organizationId,
  billingState,
  mode,
  onSubscribe,
  highlightedPlanKey,
  highlightedBillingInterval,
  variant = 'default',
}: PlanIntervalSwitcherProps) {
  const { planOptions, isLoading, hasLoaded } = useListPlanOptions(organizationId)
  const { previewAsync } = usePreviewPlanChange()
  const { changeAsync, isLoading: changeLoading } = useChangeSubscriptionPlan()

  const plansByKey = useMemo(() => {
    const map = new Map<PlanKeyEnum, PlanOption[]>()
    for (const option of planOptions) {
      const list = map.get(option.planKey) ?? []
      list.push(option)
      map.set(option.planKey, list)
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.intervalMonths - b.intervalMonths)
    }
    return map
  }, [planOptions])

  const planKeys = useMemo(() => Array.from(plansByKey.keys()), [plansByKey])

  const [selectedPlanKey, setSelectedPlanKey] = useState<PlanKeyEnum | null>(null)
  const effectivePlanKey = selectedPlanKey ?? billingState.assignedPlanKey ?? planKeys[0] ?? null

  const [pendingOption, setPendingOption] = useState<PlanOption | null>(null)
  const [resolvedPreview, setResolvedPreview] = useState<ResolvedPreview | null>(null)
  const previewGenerationRef = useRef(0)
  const activePreviewRequestRef = useRef<PreviewRequestKey | null>(null)
  const mountedRef = useRef(false)
  const activeContextRef = useRef({ organizationId, mode })
  activeContextRef.current = { organizationId, mode }

  const canManage = billingState.canManageBilling

  const resetPending = useCallback(() => {
    previewGenerationRef.current++
    activePreviewRequestRef.current = null
    setPendingOption(null)
    setResolvedPreview(null)
  }, [])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      previewGenerationRef.current++
      activePreviewRequestRef.current = null
    }
  }, [])

  // A preview is scoped to the organization and interaction mode that
  // created it. Clear the dialog immediately when either context changes and
  // invalidate any late completion from the old context.
  useEffect(() => {
    resetPending()
  }, [organizationId, mode, resetPending])

  const isCurrentRequest = (request: PreviewRequestKey) => {
    const activeContext = activeContextRef.current
    return (
      mountedRef.current &&
      activePreviewRequestRef.current === request &&
      previewGenerationRef.current === request.generation &&
      activeContext.organizationId === request.organizationId &&
      activeContext.mode === request.mode
    )
  }

  async function handleSelectInterval(option: PlanOption) {
    if (!canManage) return
    if (option.isCurrent) return

    if (mode === 'subscribe') {
      onSubscribe(option.planKey, option.billingInterval, option)
      return
    }

    const request: PreviewRequestKey = {
      organizationId,
      mode,
      planKey: option.planKey,
      billingInterval: option.billingInterval,
      generation: ++previewGenerationRef.current,
    }
    activePreviewRequestRef.current = request
    setPendingOption(option)
    setResolvedPreview(null)
    try {
      const result = await previewAsync({
        organizationId: request.organizationId,
        planKey: request.planKey,
        billingInterval: request.billingInterval,
      })
      if (!isCurrentRequest(request)) return
      setResolvedPreview({ request, response: result })
    } catch {
      if (!isCurrentRequest(request)) return
      toast.error("Couldn't preview that change. Please try again.")
      resetPending()
    }
  }

  async function handleConfirmChange() {
    const request = activePreviewRequestRef.current
    if (
      !pendingOption ||
      !request ||
      !resolvedPreview ||
      resolvedPreview.request !== request ||
      pendingOption.planKey !== request.planKey ||
      pendingOption.billingInterval !== request.billingInterval ||
      !isCurrentRequest(request)
    ) {
      return
    }

    try {
      await changeAsync({
        organizationId: request.organizationId,
        planKey: request.planKey,
        billingInterval: request.billingInterval,
      })
      if (!isCurrentRequest(request)) return
      toast.success('Plan updated')
      resetPending()
    } catch {
      if (!isCurrentRequest(request)) return
      toast.error("Couldn't update your plan. Please try again.")
    }
  }

  const preview =
    pendingOption &&
    resolvedPreview &&
    resolvedPreview.request === activePreviewRequestRef.current &&
    resolvedPreview.request.planKey === pendingOption.planKey &&
    resolvedPreview.request.billingInterval === pendingOption.billingInterval &&
    isCurrentRequest(resolvedPreview.request)
      ? resolvedPreview.response
      : null
  const previewLoading = pendingOption !== null && preview === null

  const loading = isLoading || !hasLoaded

  const intervalOptions = effectivePlanKey !== null ? (plansByKey.get(effectivePlanKey) ?? []) : []

  return (
    <div className="space-y-3">
      {billingState.planIsAssigned ? (
        <p className="text-sm text-muted-foreground">
          Your plan: <span className="font-medium text-foreground">{PLAN_LABELS[billingState.assignedPlanKey]}</span>
        </p>
      ) : loading ? (
        <SkeletonBar className="h-9 w-64" />
      ) : planKeys.length > 1 ? (
        <Tabs
          value={String(effectivePlanKey)}
          onValueChange={(value) => {
            resetPending()
            setSelectedPlanKey(Number(value) as PlanKeyEnum)
          }}
        >
          <TabsList>
            {planKeys.map((key) => (
              <TabsTrigger key={key} value={String(key)}>
                {PLAN_LABELS[key]}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      ) : null}

      <Skeleton data={loading ? null : intervalOptions} sample={SAMPLE_INTERVAL_OPTIONS} label="Loading plans">
        {(options) => (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {options.map((option) => {
              const perMonthCents = perMonthEquivalentCents(option)
              const isSelected =
                option.planKey === highlightedPlanKey && option.billingInterval === highlightedBillingInterval
              return (
                <button
                  key={`${option.planKey}-${option.billingInterval}`}
                  type="button"
                  onClick={() => handleSelectInterval(option)}
                  disabled={!canManage || option.isCurrent}
                  aria-pressed={isSelected}
                  className={cn(
                    'flex h-full flex-col rounded-xl border text-left transition-colors',
                    variant === 'compact' ? 'p-3' : 'p-4',
                    option.isCurrent || isSelected ? 'border-primary ring-1 ring-primary' : 'hover:border-primary/50',
                    !canManage || option.isCurrent ? 'cursor-not-allowed opacity-80' : 'cursor-pointer'
                  )}
                >
                  <div className="flex min-h-6 items-start justify-between gap-2">
                    <p className="flex items-center gap-1.5 text-sm font-medium">
                      {isSelected ? (
                        <span
                          aria-hidden="true"
                          className="flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground"
                        >
                          <Check className="h-3 w-3" />
                        </span>
                      ) : null}
                      {INTERVAL_LABELS[option.billingInterval]}
                    </p>
                    {option.isCurrent ? (
                      <Badge variant="secondary" className="shrink-0">
                        Current
                      </Badge>
                    ) : option.savingsLabel ? (
                      <Badge variant="success" className="shrink-0">
                        {option.savingsLabel}
                      </Badge>
                    ) : null}
                  </div>
                  <p className={cn('mt-2 font-bold', variant === 'compact' ? 'text-lg' : 'text-xl')}>
                    {option.priceFormatted}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">plus applicable tax</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    ${formatCurrency(perMonthCents)}/mo equivalent
                  </p>
                </button>
              )
            })}
          </div>
        )}
      </Skeleton>

      <Dialog open={!!pendingOption} onOpenChange={(open) => !open && resetPending()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm plan change</DialogTitle>
            {preview && <DialogDescription>{preview.description}</DialogDescription>}
          </DialogHeader>
          {previewLoading ? (
            <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Calculating…
            </div>
          ) : preview ? (
            <p className="text-sm text-muted-foreground">
              {preview.appliesImmediately
                ? 'This takes effect immediately.'
                : `This takes effect at renewal, on ${formatFullDate(preview.effectiveAt)}.`}
            </p>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={resetPending} disabled={changeLoading}>
              Cancel
            </Button>
            <Button onClick={handleConfirmChange} disabled={changeLoading || previewLoading || !preview}>
              {changeLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
